<template>
    <HomeSectionContainer v-if="feedbackQuestionHistory?.length > 0" title="My Feedback" subtitle="Nadia can gather rich feedback on your behalf.">
        <template #actions>
            <BaseButton theme="primary" @click="goToCreationMode">
                <div class="flex items-center gap-2">
                    <i class="bi bi-plus-lg font-bold" aria-hidden="true"></i>
                    <span class="body-small font-normal">New Request</span>
                </div>
            </BaseButton>
        </template>
        <div class="flex flex-col gap-4">
            <FeedbackRequestCard
                v-for="feedback in feedbackQuestionHistory"
                :key="feedback.id"
                :feedback="feedback"
                :button-text="buttonText"
                :url-button-text="urlButtonText"
                :minimum-response-count="minimumResponseCount"
                @delete="confirmDeleteIntentions"
                @copy-link="(id: number) => copyLink(id, 'list')"
                @copy-url="(id: number) => copyUrl(id, 'list')"
                @close-and-review="confirmConcludeAndReviewIntentions"
                @view-report="goToFeedbackReportFlow"
                @reopen="confirmReopenIntentions" />
        </div>
        <FeedbackConfirmationDialog
            v-model:open="showConfirmationDialog"
            :dialog-title="confirmationDialogTitle"
            :dialog-text="confirmationDialogText"
            :button-text="confirmationButtonText"
            @confirm="
                // hate doing this, but stubbed until useFeedback composable is typed
                confirmationButtonCallback as unknown as () => unknown
            " />

        <FeedbackCreationModal
            v-model:open="showFeedbackDialog"
            :show-list-view="false"
            :feedback-questions="feedbackQuestionHistory"
            :button-text="buttonText"
            :url-button-text="urlButtonText"
            :minimum-response-count="minimumResponseCount"
            @delete="confirmDeleteIntentions"
            @copy-link="copyLink"
            @copy-url="copyUrl"
            @close-and-review="confirmConcludeAndReviewIntentions"
            @view-report="goToFeedbackReportFlow"
            @reopen="confirmReopenIntentions"
            @question-created="fetchFeedback" />
    </HomeSectionContainer>
</template>

<script setup lang="ts">
import HomeSectionContainer from "~vue/ChatHome/HomeSectionContainer.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import { useFeedback } from "~vue/composables/useFeedback";
import FeedbackConfirmationDialog from "~vue/Feedback/FeedbackConfirmationDialog.vue";
import FeedbackCreationModal from "~vue/Feedback/FeedbackCreationModal.vue";
import FeedbackRequestCard from "~vue/Feedback/FeedbackRequestCard.vue";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { minimumResponseCount = 3 } = defineProps<{
    minimumResponseCount?: number;
}>();

const {
    confirmationDialogTitle,
    confirmationDialogText,
    confirmationButtonText,
    showConfirmationDialog,
    confirmationButtonCallback,
    goToFeedbackReportFlow,
    confirmDeleteIntentions,
    confirmConcludeAndReviewIntentions,
    confirmReopenIntentions,
    feedbackQuestionHistory,
    fetchFeedback,
    buttonClicked,
    copyLink,
    copyUrl,
    urlButtonClicked,
} = useFeedback();

const buttonText = computed(() => (buttonClicked.value ? t("feedback.request.copied") : t("feedback.request.copyInvite")));
const urlButtonText = computed(() => (urlButtonClicked.value ? t("feedback.request.copied") : t("feedback.request.copyUrl")));

const showFeedbackDialog = ref(false);

const goToCreationMode = () => {
    logUserInteraction("feedback_gathering", {});
    showFeedbackDialog.value = true;
};

onMounted(() => {
    fetchFeedback();
});
</script>
